
import {
  createTemplate as createTemplateApi,
  saveEventAsTemplate as saveEventAsTemplateApi,
  getTemplates as getTemplatesApi,
  getTemplate as getTemplateApi,
  getTemplateSection as getTemplateSectionApi,
  updateTemplate as updateTemplateApi,
  updateTemplateSection as updateTemplateSectionApi,
  deleteTemplate as deleteTemplateApi,
  applyTemplate as applyTemplateApi,
  getEventSectionPreview as getEventSectionPreviewApi,
  type ApplyTemplateOverrides,
} from "@/lib/api/clients/EventTemplateClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import type { CreateEventResponse } from "@/lib/api/clients/EventClient";
import {
  EventTemplate,
  TemplateSectionType,
} from "@/types/EventTemplate";
import { EventRoleCreationDTO } from "@/types/EventRoleCreationDTO";
import { isValidObjectId } from "@/lib/utils/input-validator";

export async function createTemplate(
  template: Partial<EventTemplate>,
): Promise<string | ServerError> {
  try {
    return await createTemplateApi(template);
  } catch (error) {
    console.error("Error in template handler:", error);
    return handleErrorServer(error);
  }
}

export async function saveEventAsTemplate(
  eventId: string,
  name: string,
  description: string,
  includedSections: TemplateSectionType[],
): Promise<EventTemplate | ServerError> {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!name || name.trim().length < 1) throw new Error("validationError");

    return await saveEventAsTemplateApi(
      eventId,
      name,
      description,
      includedSections,
    );
  } catch (error) {
    console.error("Error in template handler:", error);
    return handleErrorServer(error);
  }
}

export async function getTemplates(
  page: number = 0,
  size: number = 50,
  searchTerm: string = "",
): Promise<EventTemplate[] | ServerError> {
  try {
    return await getTemplatesApi(page, size, searchTerm);
  } catch (error) {
    console.error("Error in template handler:", error);
    return handleErrorServer(error);
  }
}

export async function getTemplate(
  templateId: string,
): Promise<EventTemplate | ServerError> {
  try {

    if (!isValidObjectId(templateId)) throw new Error("validationError");

    return await getTemplateApi(templateId);
  } catch (error) {
    console.error("Error in template handler:", error);
    return handleErrorServer(error);
  }
}

export async function getTemplateSection(
  templateId: string,
  section: TemplateSectionType,
): Promise<Partial<EventTemplate> | ServerError> {
  try {

    if (!isValidObjectId(templateId)) throw new Error("validationError");

    return await getTemplateSectionApi(templateId, section);
  } catch (error) {
    console.error("Error in template handler:", error);
    return handleErrorServer(error);
  }
}

export async function updateTemplate(
  templateId: string,
  updates: Partial<EventTemplate>,
): Promise<void | ServerError> {
  try {

    if (!isValidObjectId(templateId)) throw new Error("validationError");

    await updateTemplateApi(templateId, updates);
  } catch (error) {
    console.error("Error in template handler:", error);
    return handleErrorServer(error);
  }
}

export async function updateTemplateSection(
  templateId: string,
  section: TemplateSectionType,
  data: unknown,
): Promise<void | ServerError> {
  try {

    if (!isValidObjectId(templateId)) throw new Error("validationError");

    await updateTemplateSectionApi(templateId, section, data);
  } catch (error) {
    console.error("Error in template handler:", error);
    return handleErrorServer(error);
  }
}

export async function deleteTemplateAction(
  templateId: string,
): Promise<void | ServerError> {
  try {

    if (!isValidObjectId(templateId)) throw new Error("validationError");

    await deleteTemplateApi(templateId);
  } catch (error) {
    console.error("Error in template handler:", error);
    return handleErrorServer(error);
  }
}

export async function applyTemplate(
  templateId: string,
  overrides: ApplyTemplateOverrides,
  extraCollaborators: EventRoleCreationDTO[] = [],
  image?: { buffer: ArrayBuffer; fileName: string; mimeType: string },
  /** Art. 50 KI-VO: the wizard's AI lane, forwarded as `?aiContentTool=`. */
  aiContentTool?: string,
): Promise<CreateEventResponse | ServerError> {
  try {

    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!overrides.name || overrides.name.trim().length < 1)
      throw new Error("validationError");

    return await applyTemplateApi(
      templateId,
      overrides,
      extraCollaborators,
      image,
      aiContentTool,
    );
  } catch (error) {
    console.error("Error in template handler:", error);
    return handleErrorServer(error);
  }
}

export async function getEventSectionPreview(
  eventId: string,
  section: TemplateSectionType,
): Promise<unknown | ServerError> {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await getEventSectionPreviewApi(eventId, section);
  } catch (error) {
    console.error("Error in template handler:", error);
    return handleErrorServer(error);
  }
}
